import {
	Button,
	ButtonGroup,
	Card,
	Heading,
	IconButton,
	LuIcon,
	Menu,
	Separator,
	Text,
	toast,
} from "@overmap-ai/blocks"
import {
	ERROR_MSG_UNEXPECTED,
	type IssueTypePriority,
	type IssueTypeStatus,
	OrganizationAccessLevel,
	pluralize,
	type Stored,
	toFileNameSafeString,
	type User,
} from "@overmap-ai/common"
import {
	APIError,
	selectIssueTypeById,
	selectIssueTypeIdentifiersOfIssueType,
	selectIssueTypePrioritiesOfIssueType,
	selectIssueTypeStatusesOfIssueType,
	selectLatestIssueTypeFieldsOfIssueType,
	selectUsersOfProject,
} from "@overmap-ai/core"
import { FieldSchema } from "@overmap-ai/forms"
import { useOpenState } from "@overmap-ai/hooks"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import type { InitialTableState, SortingState, Updater, VisibilityState } from "@tanstack/react-table"
import { saveAs } from "file-saver"
import Papa from "papaparse"
import { memo, useCallback, useMemo } from "react"
import { useIssueTable, useIssueTableColumns } from "src/components/Tables"

import { AddIssueButton } from "../../components/Buttons/AddIssueButton"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { IssuePdfExportOptionsDialog } from "../../components/Dialogs/IssuePdfExportOptionsDialog"
import { IssueDataFilterMenu } from "../../components/FilterMenus/IssueDataFilterMenu"
import { IssuePriorityPieChart } from "../../components/Graphs/IssuePriorityPieChart"
import { IssueStatusPieChart } from "../../components/Graphs/IssueStatusPieChart"
import { IssueTypeIcon } from "../../components/Icons/IssueTypeIcon"
import { AssigneeGroup } from "../../components/MenuGroups/AssigneeGroup"
import { IssuePriorityGroup } from "../../components/MenuGroups/IssuePriorityGroup"
import { IssueTypeStatusGroup } from "../../components/MenuGroups/IssueTypeStatusGroup"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { ColumnVisibilityDropdown, PaginationControl } from "../../components/Tables/components"
import { tableIconMapping } from "../../components/Tables/mappings"
import { Table } from "../../components/Tables/Table"
import { getIssueDataCsvExporter } from "../../csv/CSVExporter/issues"
import { FilterRenderer } from "../../filtering/components"
import type { AnyFilter } from "../../filtering/filter"
import { useFilterManager, useFilters } from "../../filtering/hooks"
import { getIssueDataFilterSchema } from "../../filtering/schemas"
import { useIssueData } from "../../hooks/issues"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import {
	setIssueTypeColumnSorting,
	setIssueTypeColumnVisibility,
	setIssueTypeFilters,
	useAppDispatch,
	useAppSelector,
	useShallowEqaulSelector,
} from "../../store"
import {
	selectColumnSortingOfIssueType,
	selectColumnVisibilityOfIssueType,
	selectIssueTypeFiltersById,
} from "../../store/selectors"
import { selectIssuesOfProjectAndIssueType } from "../../store/selectors/issues"
import type { IssueData } from "../../typings"
import type { IssuePdfExportOptions } from "../../typings/reports"

enum BulkIssueActionMenuPages {
	INITIAL = "initial",
	STATUS = "status",
	PRIORITY = "priority",
	ASSIGNEE = "assignee",
}

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/issue-types/$issueTypeId")

const rowsPerPageOptions = [25, 50, 100, 200]

const initialTableState: InitialTableState = {
	sorting: [
		{
			id: "submitted_at",
			desc: true,
		},
	],
	pagination: {
		pageSize: rowsPerPageOptions[0],
	},
}

export const IssueTypeDetailView = memo(() => {
	const { issueTypeId, projectId } = routeApi.useParams()

	const issueType = useAppSelector(selectIssueTypeById(issueTypeId))

	if (!issueType) {
		throw new Error(`Issue type ${issueTypeId} not found`)
	}

	const dispatch = useAppDispatch()
	const latestIssueTypeFields = useAppSelector(selectLatestIssueTypeFieldsOfIssueType(issueType.uuid))
	const serializedFilters = useAppSelector(selectIssueTypeFiltersById(issueType.uuid))
	const columnSorting = useAppSelector(selectColumnSortingOfIssueType(issueType.uuid))
	const columnVisibility = useAppSelector(selectColumnVisibilityOfIssueType(issueType.uuid))
	const issueTypeIdentifiers = useShallowEqaulSelector(selectIssueTypeIdentifiersOfIssueType(issueType.uuid))
	const issueTypePriorities = useShallowEqaulSelector(selectIssueTypePrioritiesOfIssueType(issueType.uuid))
	const issueTypeStatuses = useShallowEqaulSelector(selectIssueTypeStatusesOfIssueType(issueType.uuid))
	const users = useShallowEqaulSelector(selectUsersOfProject(projectId))
	const navigate = useNavigate()
	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	const issues = useShallowEqaulSelector(selectIssuesOfProjectAndIssueType(projectId, issueType.uuid))
	const issueData = useIssueData(issues)

	const fieldSchema = useMemo(
		() => FieldSchema.deserialize(latestIssueTypeFields?.fields ?? []),
		[latestIssueTypeFields?.fields],
	)

	const filterSchema = useMemo(
		() =>
			getIssueDataFilterSchema({
				assignees: users,
				issueTypeIdentifiers,
				issueTypeFieldSchema: fieldSchema,
				issueTypePriorities,
				issueTypeStatuses,
				users,
			}),
		[fieldSchema, issueTypeIdentifiers, issueTypePriorities, issueTypeStatuses, users],
	)

	const filters = useMemo(() => {
		return filterSchema.deserializeFilters(serializedFilters)
	}, [filterSchema, serializedFilters])

	const handleFiltersChange = useCallback(
		(filters: AnyFilter[]) => {
			dispatch(
				setIssueTypeFilters({
					id: issueType.uuid,
					filters: filterSchema.serializeFilters(filters),
				}),
			)
		},
		[dispatch, filterSchema, issueType.uuid],
	)

	const handleColumnVisibilityChange = useCallback(
		(updater: Updater<VisibilityState>) => {
			dispatch(
				setIssueTypeColumnVisibility({
					id: issueType.uuid,
					columnVisibility: typeof updater === "function" ? updater(columnVisibility) : updater,
				}),
			)
		},
		[dispatch, issueType.uuid, columnVisibility],
	)

	const handleSortingChange = useCallback(
		(updater: Updater<SortingState>) => {
			dispatch(
				setIssueTypeColumnSorting({
					id: issueType.uuid,
					columnSorting: typeof updater === "function" ? updater(columnSorting) : updater,
				}),
			)
		},
		[dispatch, issueType.uuid, columnSorting],
	)

	const filterManager = useFilterManager({
		filterSchema,
		filters,
		onFilterChange: handleFiltersChange,
	})

	const filteredData = useFilters({
		filters: filters,
		data: issueData,
		filterManager: filterManager,
	})

	const columns = useIssueTableColumns({
		issueTypeFieldSchema: fieldSchema,
		issueTypeStatuses,
		issueTypePriorities,
		issueTypeIdentifiers,
	})

	const table = useIssueTable({
		columns,
		data: filteredData,
		enableRowSelection: true,
		initialState: initialTableState,
		state: {
			columnVisibility,
			sorting: columnSorting,
		},
		onColumnVisibilityChange: handleColumnVisibilityChange,
		onSortingChange: handleSortingChange,
	})

	const handleRowClick = useCallback(
		(issueData: IssueData) => {
			void navigate({
				from: "/$organizationId/$projectId",
				to: "/$organizationId/$projectId/issues/$issueId",
				params: (prev) => ({
					...prev,
					issueId: issueData.issue.uuid,
				}),
			})
		},
		[navigate],
	)

	const handleEdit = useCallback(() => {
		void navigate({
			from: "/$organizationId/$projectId/issue-types/$issueTypeId",
			to: "/$organizationId/settings/issue-types/$issueTypeId",
		})
	}, [navigate])

	const [showPdfExportOptionsDialog, openPdfExportOptionsDialog, closePdfExportOptionsDialog] = useOpenState(false)

	const handleExportToPdf = useCallback(
		(options: IssuePdfExportOptions) => {
			const selectedRows = table.getSelectedRowModel().rows.map((row) => row.original.issue.uuid)

			void sdk.issues
				.getIssuesPdfReport(issueType.uuid, selectedRows, options)
				.then(() => {
					toast.success({
						title: "Report requested",
						description: `Requested report for ${selectedRows.length} ${pluralize("issue", selectedRows.length)}. Visit the Reports page when it is ready.`,
					})
				})
				.catch((error: unknown) => {
					toast.error({
						title: "Failed to generate report",
						description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
					})
				})
		},
		[table, issueType.uuid],
	)

	const exportToCSV = useCallback(() => {
		const exporter = getIssueDataCsvExporter(
			table.getSortedRowModel().rows.map((row) => row.original),
			{
				issueTypeStatuses: issueTypeStatuses,
				issueTypePriorities: issueTypePriorities,
				issueTypeIdentifiers: issueTypeIdentifiers,
				issueTypeFieldSchema: fieldSchema,
				users: users,
			},
		)

		const csvData = Papa.unparse(exporter.toPapaUnparseFormat())
		const blob = new Blob([csvData], { type: "text/csv" })
		saveAs(blob, toFileNameSafeString(issueType.name, ".csv"))
	}, [table, fieldSchema, issueTypeIdentifiers, issueType.name, issueTypePriorities, issueTypeStatuses, users])

	const selectedRowCount = table.getSelectedRowModel().rows.length
	const assignees = useMemo(() => [null, ...users], [users])

	const getSelectedIssueIds = useCallback(
		() => table.getSelectedRowModel().rows.map((row) => row.original.issue.uuid),
		[table],
	)

	const handleBulkStatusChange = useCallback(
		(issueTypeStatus: Stored<IssueTypeStatus>) => {
			const issueIds = getSelectedIssueIds()
			sdk.issues.bulkUpdate(issueIds.map((uuid) => ({ uuid, status: issueTypeStatus.uuid })))
		},
		[getSelectedIssueIds],
	)

	const handleBulkPriorityChange = useCallback(
		(priority: Stored<IssueTypePriority>) => {
			const issueIds = getSelectedIssueIds()
			sdk.issues.bulkUpdate(issueIds.map((uuid) => ({ uuid, priority: priority.uuid })))
		},
		[getSelectedIssueIds],
	)

	const handleBulkAssigneeChange = useCallback(
		(user: User | null) => {
			const issueIds = getSelectedIssueIds()
			sdk.issues.bulkUpdate(issueIds.map((uuid) => ({ uuid, assigned_to: user?.id ?? null })))
		},
		[getSelectedIssueIds],
	)

	const handleBulkDelete = useCallback(() => {
		const issueIds = getSelectedIssueIds()

		openConfirmDeleteDialog({
			title: "Delete issue(s)",
			description: `The ${issueIds.length} issue(s) will be deleted. This action cannot be undone.`,
			onAction: () => void sdk.issues.bulkDelete(issueIds),
		})

		table.resetRowSelection()
	}, [openConfirmDeleteDialog, getSelectedIssueIds, table])

	return (
		<ViewContainer>
			<div className="flex size-full flex-col gap-4 overflow-y-hidden">
				<div className="flex w-full items-center gap-2">
					<Heading className="flex h-max min-w-0 grow items-center gap-2" size="xl">
						<IssueTypeIcon issueTypeId={issueType.uuid} />
						<span className="truncate">{issueType.name}</span>
					</Heading>

					<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.BASIC} type="hide">
						<AddIssueButton size="sm" variant="surface" issueType={issueType} />
					</OrganizationAccessRequired>
					<Menu.Root>
						<Menu.ClickTrigger>
							<IconButton accentColor="base" size="sm" variant="ghost">
								<LuIcon icon={modelIconMapping.more} />
							</IconButton>
						</Menu.ClickTrigger>
						<Menu.Content size="sm">
							<Menu.Item onSelect={handleEdit}>
								<LuIcon icon={modelIconMapping.edit} />
								Edit
							</Menu.Item>
						</Menu.Content>
					</Menu.Root>
				</div>

				<Separator size="full" />

				<div className="flex size-full flex-col gap-4 overflow-y-auto overflow-x-hidden">
					<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
						<Card className="flex flex-col gap-2" size="sm">
							<Text>Status distribution</Text>
							<Text size="xs" accentColor="base">
								Distribution of issues by status.
							</Text>
							<IssueStatusPieChart issues={issues} issueTypeStatuses={issueTypeStatuses} height={250} />
						</Card>

						<Card className="flex flex-col gap-2" size="sm">
							<Text>Priority distribution</Text>
							<Text size="xs" accentColor="base">
								Distribution of issues by priority.
							</Text>
							<IssuePriorityPieChart
								issues={issues}
								issueTypePriorities={issueTypePriorities}
								height={250}
							/>
						</Card>
					</div>

					<Card className="flex flex-col gap-2" size="sm">
						<ButtonGroup
							className="flex items-center justify-between"
							size="xs"
							variant="soft"
							accentColor="base"
						>
							<div className="flex items-center gap-2">
								<Text>Issues</Text>
								<IssueDataFilterMenu
									filterManager={filterManager}
									assignees={users}
									issueTypeIdentifiers={issueTypeIdentifiers}
									issueTypePriorities={issueTypePriorities}
									issueTypeStatuses={issueTypeStatuses}
									issueTypeFieldSchema={fieldSchema}
									users={users}
									size="sm"
									align="start"
								>
									<Button>
										<LuIcon icon={modelIconMapping.filter} />
										Filter
									</Button>
								</IssueDataFilterMenu>

								{filterManager.getFilters().length > 0 && (
									<IconButton onClick={filterManager.removeAll}>
										<LuIcon icon={modelIconMapping.close} />
									</IconButton>
								)}
							</div>
							<div className="flex gap-2">
								{selectedRowCount > 0 ? (
									<>
										<Menu.Root>
											<Menu.ClickTrigger>
												<Button>
													<LuIcon icon={modelIconMapping.command} />
													Actions
												</Button>
											</Menu.ClickTrigger>
											<Menu.Content size="sm">
												<Menu.Scroll>
													<Menu.Pages defaultPage={BulkIssueActionMenuPages.INITIAL}>
														<Menu.PageContent page={BulkIssueActionMenuPages.INITIAL}>
															<Menu.PageTrigger page={BulkIssueActionMenuPages.STATUS}>
																<LuIcon icon={modelIconMapping.status} />
																Change status
															</Menu.PageTrigger>
															<Menu.PageTrigger page={BulkIssueActionMenuPages.PRIORITY}>
																<LuIcon icon={modelIconMapping.priority} />
																Change priority
															</Menu.PageTrigger>
															<Menu.PageTrigger page={BulkIssueActionMenuPages.ASSIGNEE}>
																<LuIcon icon={modelIconMapping.user} />
																Change assignee
															</Menu.PageTrigger>
															<Menu.Separator />
															<Menu.Item onSelect={openPdfExportOptionsDialog}>
																<LuIcon icon={modelIconMapping.download} />
																Export PDF
															</Menu.Item>
															<Menu.Item onSelect={exportToCSV}>
																<LuIcon icon={modelIconMapping.download} />
																Export CSV
															</Menu.Item>
															<OrganizationAccessRequired
																accessLevel={OrganizationAccessLevel.BASIC}
																type="hide"
															>
																<Menu.Separator />
																<Menu.Item onSelect={handleBulkDelete}>
																	<LuIcon icon={modelIconMapping.delete} />
																	Delete
																</Menu.Item>
															</OrganizationAccessRequired>
														</Menu.PageContent>
														<Menu.PageContent page={BulkIssueActionMenuPages.STATUS}>
															<IssueTypeStatusGroup
																issueTypeStatuses={issueTypeStatuses}
																onSelectIssueTypeStatus={handleBulkStatusChange}
															/>
														</Menu.PageContent>
														<Menu.PageContent page={BulkIssueActionMenuPages.PRIORITY}>
															<IssuePriorityGroup
																priorities={issueTypePriorities}
																onSelectPriority={handleBulkPriorityChange}
															/>
														</Menu.PageContent>
														<Menu.PageContent page={BulkIssueActionMenuPages.ASSIGNEE}>
															<AssigneeGroup
																assignees={assignees}
																onSelectAssignee={handleBulkAssigneeChange}
															/>
														</Menu.PageContent>
													</Menu.Pages>
												</Menu.Scroll>
											</Menu.Content>
										</Menu.Root>
									</>
								) : null}

								<ColumnVisibilityDropdown table={{ ...table }} align="end" size="sm">
									<IconButton>
										<LuIcon icon={tableIconMapping.columns} />
									</IconButton>
								</ColumnVisibilityDropdown>
							</div>
						</ButtonGroup>

						<FilterRenderer filterManager={filterManager} size="sm" />

						<div className="w-full overflow-x-auto">
							<Table
								className="w-full"
								size="sm"
								variant="surface"
								table={{ ...table }}
								onRowClick={handleRowClick}
								compact
								interactive
								emptyText="No issues."
							/>
						</div>

						<PaginationControl<IssueData> table={{ ...table }} rowPerPageOptions={rowsPerPageOptions} />
					</Card>
				</div>
			</div>
			<IssuePdfExportOptionsDialog
				key={issueType.uuid}
				issueTypeId={issueType.uuid}
				onSubmitOptions={handleExportToPdf}
				open={showPdfExportOptionsDialog}
				onOpenChange={closePdfExportOptionsDialog}
			/>
		</ViewContainer>
	)
})
IssueTypeDetailView.displayName = "IssueTypeDetailView"
